import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Flex,
} from 'antd';
import classNames from 'classnames';
import {
	CheckOutlined,
	CloseOutlined,
	EditOutlined,
} from '@ant-design/icons';
import { PortActionTypes } from '@shared/utils/constants';
import { isPortCallCommencementDto } from '@shared/utils/portCallHelpers';
import type { Port } from '@api/utils/ports';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import {
	deleteItineraryEntry,
	updatePortCall,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import CountryFlag from '@client/components/CountryFlag';
import Select from '@client/components/Select';
import Button from '@client/components/Button';
import { useMiscHandlers } from '@client/screens/estimates/details/context/hooks/useMiscHandlers';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import { useItinerary } from './ItineraryTab/ItineraryProvider';
import styles from './ItineraryTab/tabs/styles/SummaryTab.module.css';

const PortCallSelector = () => {
	const [updatedPort, setUpdatedPort] = useState<Port | undefined>();
	const [editingPort, setEditingPort] = useState(false);
	const [saving, setSaving] = useState(false);
	const {
		expandedEntry,
		refreshItinerary,
		setItineraryShouldUpdate,
		onSelectEntry,
		portCalls,
	} = useItinerary();

	const {
		refreshDetails,
	} = useVoyage();

	const {
		ports
	} = useMiscHandlers();

	const selectedEntry = expandedEntry as ItineraryPortCallDto;

	const actions = selectedEntry?.actions ?? [];
	const actionStr = actions.map((a) => a.event);
	const isCommencement = isPortCallCommencementDto(selectedEntry);
	const isLoadingOrDischarge =
		actionStr.includes(PortActionTypes.LOADING) ||
		actionStr.includes(PortActionTypes.DISCHARGING);

	const disabledDeleteTooltip = useMemo(() => {
		if (isLoadingOrDischarge) {
			return 'You cannot delete port calls with loading or discharging actions';
		}

		if (isCommencement) {
			return 'You cannot delete the commencement port call';
		}

		return '';
	}, [isCommencement, isLoadingOrDischarge]);

	const {
		arrivalDate,
		departureDate,
		requiredArrivalDate,
		estimatedArrivalDate,
	} = selectedEntry;

	const isActual = departureDate != null || arrivalDate != null;

	const hasDaExpenses = selectedEntry.voyageExpenses.length > 0;

	const deletePortCall = async () => {
		try {
			await deleteItineraryEntry(selectedEntry.vesselId, selectedEntry.id);
			refreshDetails();
			await refreshItinerary();
			setItineraryShouldUpdate(true);
		} catch (e) {
			showErrorNotification('Could not delete port call', e as Error);
		}
	};

	const updateEntry = useCallback(
		async (
			attributes: {
				port?: Port;
				mergeChangedPort?: boolean;
			},
		) => {
			setSaving(true);

			try {
				onSelectEntry(undefined);
				await updatePortCall(selectedEntry.vesselId, selectedEntry.id, attributes, true);
				refreshDetails();
				await refreshItinerary();
				setItineraryShouldUpdate(true);
			} catch (e) {
				showErrorNotification('Could not update port call', e as Error);
			} finally {
				setSaving(false);
			}
		}, [
			onSelectEntry,
			refreshDetails,
			refreshItinerary,
			selectedEntry.id,
			selectedEntry.vesselId,
			setItineraryShouldUpdate,
		],
	);

	const portOptions = useMemo(() => (ports != null ? ports?.map((p) => {
		return {
			label: (
				<div key={p.id} className={styles.portSelector}>
					<CountryFlag
						countryCode={typeof p?.countryCode === 'string' ? p.countryCode : undefined}
					/>
					<p>{p.name}</p>
				</div>
			),
			value: p.id,
			searchValue: p.name,
		};
	}).sort((a, b) => a.searchValue.localeCompare(b.searchValue)) : []), [ports]);

	const handlePortSelect = async (id: number | null) => {
		if (id == null) {
			return;
		}

		const newPort = ports?.find((p) => p.id === id);

		if (newPort == null) {
			return;
		}

		setUpdatedPort(newPort);
	};

	const handlePortChangeSave = useCallback(async (merge: boolean = false) => {
		await updateEntry({
			port: updatedPort,
			mergeChangedPort: merge,
		});
		refreshDetails();
		setEditingPort(false);
		setUpdatedPort(undefined);
	}, [refreshDetails, updateEntry, updatedPort]);

	const etaAfterRequiredAlert = useMemo(() => {
		if (requiredArrivalDate?.isBefore(estimatedArrivalDate) && arrivalDate == null) {
			return (
				<Alert type="warning" message="Required arrival date is before estimated arrival date" />
			);
		}

		return null;
	}, [arrivalDate, estimatedArrivalDate, requiredArrivalDate]);

	const savePortChangeContent = useMemo(() => {
		const exists = portCalls.slice(1).find((pc) => pc.port.id === updatedPort?.id);

		if (exists) {
			return (
				<Flex vertical gap={10}>
					<Alert
						message="There's another itinerary entry with this port -
						do you want to merge these entries or keep the port calls separate?"
					/>
					<Flex gap={10}>
						<Button
							type="primary"
							onClick={() => handlePortChangeSave(true)}
						>
							Merge
						</Button>
						<Button
							onClick={() => handlePortChangeSave()}
						>
							Keep separate
						</Button>
					</Flex>
				</Flex>
			);
		}

		return (
			<Flex>
				<Button
					type="link"
					className={styles.saveIcon}
					onClick={() => handlePortChangeSave()}
					icon={(
						<CheckOutlined />
					)}
				/>
				<Button
					type="link"
					className={styles.closeIcon}
					onClick={() => {
						setEditingPort(false);
						setUpdatedPort(undefined);
					}}
					icon={(
						<CloseOutlined />
					)}
				/>
			</Flex>
		);
	}, [portCalls, handlePortChangeSave, updatedPort?.id]);

	return (
		<Flex>
			<div
				className={classNames(styles.flagAndNameRow)}
			>
				{editingPort ? (
					<Select
						showSearch
						className={styles.selectPort}
						size="large"
						value={selectedEntry.port.id}
						optionFilterProp="searchValue"
						options={portOptions}
						onSelect={handlePortSelect}
					/>
				) : (
					<>
						<CountryFlag
							countryCode={selectedEntry?.port.countryCode}
							className={styles.portFlag}
							size={35}
						/>
						{selectedEntry.port.name}
					</>
				)}
			</div>
			<div
				className={styles.topButtonRow}
			>
				{editingPort ?
					savePortChangeContent :
					(
						<div className={styles.buttonContainer}>
							{!selectedEntry.isCanalTransit ? (
								<div className={styles.innerBtnContainer}>
									<Button
										type="link"
										loading={saving}
										onClick={() => {
											setUpdatedPort(undefined);
											setEditingPort(true);
										}}
										disabled={isActual}
										icon={(
											<EditOutlined />
										)}
									>
										Change port
									</Button>
									{etaAfterRequiredAlert}
								</div>
							) : (<div />)}
							{(arrivalDate == null && departureDate == null) && (
								<Button
									type="primary"
									danger
									disabled={isCommencement || isLoadingOrDischarge}
									onClick={() => deletePortCall()}
									confirmTitle={`
										Are you sure you want to delete this port call? ${hasDaExpenses != null && 
										'Port DA\'s attached to this port call will be deleted'}`}
									disabledTooltip={disabledDeleteTooltip}
								>
									Delete port call
								</Button>
							)}
						</div>
					)}
			</div>
		</Flex>
	);
};

export default PortCallSelector;
